import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import ReCAPTCHA from 'react-google-recaptcha';
import PhoneInput, {
  isValidPhoneNumber,
} from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Loader2,
  Eye,
  EyeOff,
  Check,
  Sparkles,
  Sun,
  Moon,
  Laptop,
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import {
  register as registerAction,
  registerCheck,
  sendVerificationCode,
  verifyPhoneNumber,
} from '@/store/actions/creators/authActions';
import { fetchRegistrationSetting } from '@/store/actions/creators/registrationSettingActions';
import { registrationSettingSelector } from '@/store/selectors/registrationSettingSelector';
import {
  registrationStatusSelector,
  registrationErrorSelector,
  sendVerificationCodeErrorSelector,
  verificationCodeSendStatusSelector,
  phoneNumberVerificationStatusSelector,
} from '@/store/selectors/authSelector';
import { FAILED, STARTED, SUCCEEDED } from '@/utils/helpers/store-helpers';
import { errorMessageToast, getError } from '@/utils/common/helper';
import { pushEvent } from '@/utils/gtm';
import registerPageContentService from '@/services/registerPageContent';
import authService from '@/services/auth';

import { useTheme } from '@/components/ThemeProvider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select as ShSelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import ConfirmationModal from '@/components/ConfirmationModal';
import Hint from '@/components/ResetPassword/Hint';
import { getFlag } from '@/components/flags';
import LogoLight from '@/assets/images/logo_smsreflex_light.png';
import LogoDark from '@/assets/images/logo_smsreflex_dark.png';
import { cn } from '@/lib/utils';

const PWD_REGEX =
  /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[!@#$%^&*()\-_=+;:'"<,>.]).{8,}$/;
const LOGIN_REGEX = /^(?=.*[a-zA-Z])[a-zA-Z0-9@.+]{6,}$/;
// Format email pragmatique (pas RFC 5322 complet, mais couvre 99% des cas):
// local@domain.tld avec local alphanum + .-_+%, domain alphanum + .-,
// tld au moins 2 chars.
const EMAIL_REGEX = /^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/;

type Variant = 'sections' | 'steps';

type Bullet = { icon?: string; title?: string; desc?: string };
type RegisterContent = {
  id: number;
  language: string;
  flagCode: string;
  languageLabel?: string;
  headline?: string;
  subtitle?: string;
  bullets?: Bullet[];
  ctaText?: string;
  displayOrder: number;
  isActive: boolean;
};

function resolveIcon(name?: string): LucideIcon {
  if (!name) return Sparkles;
  const map = LucideIcons as unknown as Record<string, LucideIcon>;
  return (map[name] as LucideIcon) ?? Sparkles;
}

// ---------------------------------------------------------------------------
// Stepper
// ---------------------------------------------------------------------------
type StepperLabel = { key: number; label: string };

function Stepper({ current, labels }: { current: number; labels: StepperLabel[] }) {
  return (
    <div className="mb-6 flex items-center gap-3">
      {labels.map((s, idx) => {
        const isDone = current > s.key;
        const isActive = current === s.key;
        return (
          <div key={s.key} className="flex flex-1 items-center gap-2">
            <div
              className={cn(
                'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 text-xs font-semibold transition-colors',
                isDone
                  ? 'border-primary bg-primary text-primary-foreground'
                  : isActive
                  ? 'border-primary text-primary'
                  : 'border-muted-foreground/30 text-muted-foreground',
              )}
            >
              {isDone ? <Check className="h-3.5 w-3.5" /> : s.key}
            </div>
            <span
              className={cn(
                'text-xs font-medium',
                isActive ? 'text-foreground' : 'text-muted-foreground',
              )}
            >
              {s.label}
            </span>
            {idx < labels.length - 1 && (
              <div
                className={cn(
                  'h-px flex-1 transition-colors',
                  isDone ? 'bg-primary' : 'bg-muted-foreground/20',
                )}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Top-right floating bar (theme + language flags)
// ---------------------------------------------------------------------------
function TopBar({
  contents,
  currentLang,
  onLangChange,
}: {
  contents: RegisterContent[];
  currentLang: string;
  onLangChange: (lang: string) => void;
}) {
  const { theme, setTheme } = useTheme();
  return (
    <div className="absolute right-4 top-4 z-20 flex items-center gap-2">
      <div className="flex items-center gap-2">
        {contents
          .filter((c) => c.isActive)
          .map((c) => {
            const Flag = getFlag(c.flagCode);
            const active = c.language === currentLang;
            return (
              <button
                key={c.language}
                type="button"
                aria-label={c.languageLabel ?? c.language}
                aria-pressed={active}
                onClick={() => {
                  pushEvent('lang_change', { from: currentLang, to: c.language, page: 'register' });
                  onLangChange(c.language);
                }}
                className={
                  'h-6 w-6 overflow-hidden rounded-full transition-all hover:scale-110 ' +
                  (active
                    ? 'ring-2 ring-offset-2 ring-primary scale-110'
                    : 'ring-1 ring-border opacity-70 hover:opacity-100')
                }
              >
                <Flag className="h-full w-full" />
              </button>
            );
          })}
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 rounded-full bg-card/90 backdrop-blur"
            aria-label="Theme"
          >
            {theme === 'dark' ? (
              <Moon className="h-4 w-4" />
            ) : theme === 'light' ? (
              <Sun className="h-4 w-4" />
            ) : (
              <Laptop className="h-4 w-4" />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => setTheme('light')}>
            <Sun className="mr-2 h-4 w-4" /> Light
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setTheme('dark')}>
            <Moon className="mr-2 h-4 w-4" /> Dark
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setTheme('system')}>
            <Laptop className="mr-2 h-4 w-4" /> System
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Marketing column (right side)
// ---------------------------------------------------------------------------
function MarketingColumn({ content }: { content: RegisterContent | null }) {
  return (
    <div className="relative hidden flex-col overflow-hidden bg-[#063ea6] p-10 text-white md:col-span-7 md:flex md:p-14 lg:p-16">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.07] [background-image:radial-gradient(white_1px,transparent_1px)] [background-size:24px_24px]"
      />
      <div className="relative flex max-w-2xl flex-1 flex-col justify-center">
        {/* No logo here — the form-side logo (top-left) is the only one,
            we don't want to brand the page twice. */}
        {content?.headline && (
          <h1 className="text-3xl font-bold leading-tight tracking-tight md:text-4xl">
            {content.headline}
          </h1>
        )}
        {content?.subtitle && (
          <p className="mt-4 text-base text-white/80 md:text-lg">{content.subtitle}</p>
        )}
        {content?.bullets && content.bullets.length > 0 && (
          <ul className="mt-10 space-y-5">
            {content.bullets.map((b, idx) => {
              const Icon = resolveIcon(b.icon);
              return (
                <li key={idx} className="flex items-start gap-4">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-white/10 ring-1 ring-white/15">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    {b.title && <p className="font-semibold leading-snug">{b.title}</p>}
                    {b.desc && <p className="mt-1 text-sm text-white/75">{b.desc}</p>}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Account fields step. Renders all 12 fields with section dividers when
// variant === 'sections', or paginated into 2 sub-steps (identity then
// auth) when variant === 'steps'. Validation logic is identical between
// variants.
// ---------------------------------------------------------------------------
type AccountStepProps = {
  inputs: any;
  setInputs: (i: any) => void;
  errors: any;
  setErrors: (e: any) => void;
  isLoading: boolean;
  setIsLoading: (b: boolean) => void;
  onAccountValidated: () => void;
  variant: Variant;
  ctaLabel?: string;
  /** Called when the inner sub-step changes (variant 'steps' only) — lets the
   *  parent update the global Stepper. */
  onSubStepChange?: (subStep: 1 | 2) => void;
};

function AccountStep({
  inputs,
  setInputs,
  errors,
  setErrors,
  isLoading,
  setIsLoading,
  onAccountValidated,
  variant,
  ctaLabel,
  onSubStepChange,
}: AccountStepProps) {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const setting = (useSelector(registrationSettingSelector) ?? {}) as any;
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const [firstName, setFirstName] = useState(inputs.firstName ?? '');
  const [lastName, setLastName] = useState(inputs.lastName ?? '');
  const [company, setCompany] = useState(inputs.company ?? '');
  const [country, setCountry] = useState(inputs.country ?? '');
  const [address, setAddress] = useState(inputs.address ?? '');
  const [city, setCity] = useState(inputs.city ?? '');
  const [zip, setZip] = useState(inputs.zip ?? '');
  const [email, setEmail] = useState(inputs.email ?? '');
  const [login, setLogin] = useState(inputs.login ?? '');
  const [password, setPassword] = useState(inputs.password ?? '');
  const [confirmPassword, setConfirmPassword] = useState(inputs.confirmPassword ?? '');
  const [showPassword, setShowPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(inputs.acceptTermsOfCondition ?? false);
  const [pwdHint, setPwdHint] = useState<any>({});
  const [loginRegexError, setLoginRegexError] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Variant 'steps' : sub-step 1 = identity / address, sub-step 2 = auth.
  const [subStep, setSubStep] = useState<1 | 2>(1);

  useEffect(() => {
    if (onSubStepChange) onSubStepChange(subStep);
  }, [subStep, onSubStepChange]);

  const countryOptions = useMemo(() => {
    const list = ((setting?.countryList ?? []) as any[]).map((c) => ({
      value: c.countryName,
      label: c.countryName,
    }));
    list.sort((a, b) => {
      if (a.value === 'France') return -1;
      if (b.value === 'France') return 1;
      return a.label.localeCompare(b.label);
    });
    return list;
  }, [setting?.countryList]);

  const fieldVisible = (key: string) => setting?.[`${key}Visible`] !== false;
  const fieldRequired = (key: string) =>
    fieldVisible(key) && setting?.[`${key}Required`] !== false;

  const onPwdChange = (value: string) => {
    setPwdHint({
      min_char: /^.{8,}$/.test(value),
      one_lowercase: /[a-z]/.test(value),
      one_uppercase: /[A-Z]/.test(value),
      one_digit: /[0-9]/.test(value),
      one_special_char: /[!@#$%^&*()\-_=+;:'"<,>.]/.test(value),
    });
    setPassword(value);
  };

  const onLoginChange = (value: string) => {
    setLoginRegexError(value.length > 0 && !LOGIN_REGEX.test(value));
    setErrors(null);
    setLogin(value);
  };

  const handleNextFromIdentity = (e: React.FormEvent) => {
    e.preventDefault();
    // Identity-step gate : block transition until every required field
    // (admin-driven flags + the always-mandatory country) is filled.
    // Without this the user could land on step 2, submit, and get an
    // opaque server-side error blaming a step 1 field.
    const err: any = {};
    if (fieldRequired('firstName') && !firstName.trim()) err.firstName = true;
    if (fieldRequired('lastName') && !lastName.trim()) err.lastName = true;
    if (fieldRequired('company') && !company.trim()) err.company = true;
    if (!country) err.country = true;
    if (fieldRequired('address') && !address.trim()) err.address = true;
    if (fieldRequired('city') && !city.trim()) err.city = true;
    if (fieldRequired('zip') && !zip.trim()) err.zip = true;
    if (!_.isEmpty(err)) {
      pushEvent('register_identity_validation_failed', {
        variant,
        errors: Object.keys(err),
      });
      setErrors(err);
      errorMessageToast(
        t('REGISTRATION.PLEASE_FIX_ERRORS') ??
          'Please fix the errors highlighted below.',
      );
      return;
    }
    setErrors(null);
    pushEvent('register_next_identity', { variant });
    // Persiste le draft d'inscription (steps 1→2) — fire-and-forget,
    // n'echoue pas le flow. Requiert email valide (sinon BE Joi rejette).
    // L'email est en step 2 donc on attend qu'il soit saisi : si vide
    // ici (variant 'steps' qui n'a pas encore email), on skip.
    if (email && /^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/.test(email.trim())) {
      authService.registerDraft({
        email: email.trim(),
        login: login || undefined,
        firstName: firstName || undefined,
        lastName: lastName || undefined,
        company: company || undefined,
        address: address || undefined,
        city: city || undefined,
        zip: zip || undefined,
        country: country || undefined,
        variant,
      }).catch(() => {/* silent */});
    }
    setSubStep(2);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const err: any = {};
    // Explicit JS-only validation. The form has `noValidate` so the
    // browser doesn't pop a "please fill this field" tooltip we can't
    // see — we surface our own error messages + a top-level toast
    // instead, so the user always knows what's blocking the submit.
    // Identity (validated again here since the 'sections' variant
    // submits everything in one shot).
    if (fieldRequired('firstName') && !firstName.trim()) err.firstName = true;
    if (fieldRequired('lastName') && !lastName.trim()) err.lastName = true;
    if (fieldRequired('company') && !company.trim()) err.company = true;
    if (!country) err.country = true;
    if (fieldRequired('address') && !address.trim()) err.address = true;
    if (fieldRequired('city') && !city.trim()) err.city = true;
    if (fieldRequired('zip') && !zip.trim()) err.zip = true;
    // Auth.
    if (!email.trim()) err.email = t('COMMON.REQUIRED') ?? 'is required';
    else if (!EMAIL_REGEX.test(email.trim()))
      err.email = t('REGISTRATION.EMAIL_INVALID') ?? 'is not a valid email address';
    if (!login.trim()) err.login = t('COMMON.REQUIRED') ?? 'is required';
    if (!password) err.password = true;
    if (password !== confirmPassword) err.password = true;
    if (!PWD_REGEX.test(password)) err.password = true;
    if (!acceptTerms) err.terms = true;
    if (!recaptchaRef.current?.getValue()) err.recaptcha = true;
    if (loginRegexError) err.login = t('ADMIN.CLIENTS.REGISTER_FIELD_REGEX_ERROR') ?? 'Invalid login format';
    if (!_.isEmpty(err)) {
      pushEvent('register_account_validation_failed', { variant, errors: Object.keys(err) });
      setErrors(err);
      errorMessageToast(
        t('REGISTRATION.PLEASE_FIX_ERRORS') ??
          'Please fix the errors highlighted below.',
      );
      return;
    }

    setIsLoading(true);
    pushEvent('register_account_submit', { variant });
    // Pour les champs admin-toggleable (company / address / city / zip), on
    // n'envoie pas la clé du tout si l'admin a masqué le champ — sinon le
    // Joi BE rejette les empty strings (and inputs spread can carry stale '').
    const data: Record<string, any> = {
      ...inputs,
      firstName,
      lastName,
      country,
      email,
      login,
      password,
      confirmPassword,
      acceptTermsOfCondition: acceptTerms,
      language: i18n.language,
    };
    const togglable = { company, address, city, zip };
    (['company', 'address', 'city', 'zip'] as const).forEach((k) => {
      if (fieldVisible(k)) {
        data[k] = togglable[k];
      } else {
        delete data[k];
      }
    });

    try {
      // @ts-expect-error legacy redux-thunk action types
      await dispatch(registerCheck(data));
      pushEvent('register_account_success', { variant });
      // Persiste le draft d'inscription avant la verification phone.
      // Fire-and-forget, n'echoue pas le flow. A ce stade on a TOUT
      // (identite + email/login), juste le phone restera a verifier.
      authService.registerDraft({
        email: data.email,
        login: data.login,
        firstName: data.firstName,
        lastName: data.lastName,
        company: data.company,
        address: data.address,
        city: data.city,
        zip: data.zip,
        country: data.country,
        variant,
      }).catch(() => {/* silent */});
      setIsLoading(false);
      setInputs(data);
      onAccountValidated();
    } catch (error: any) {
      setIsLoading(false);
      pushEvent('register_account_error', { variant });
      const e = getError(error);
      if (typeof e === 'string') {
        errorMessageToast(error?.response?.data?.message);
      } else {
        setErrors(e);
        if (!_.isEmpty((e as any)?.email) || !_.isEmpty((e as any)?.login)) {
          setShowConfirmModal(true);
        }
      }
    }
  };

  const showIdentitySection = variant !== 'steps' || subStep === 1;
  const showAuthSection = variant !== 'steps' || subStep === 2;
  const showSectionHeaders = variant === 'sections';

  const identityFields = (
    <div className="space-y-3">
      {showSectionHeaders && (
        <div className="border-b pb-1">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {t('REGISTRATION.SECTION_IDENTITY') ?? 'Identité'}
          </h3>
        </div>
      )}
      {(fieldVisible('firstName') || fieldVisible('lastName')) && (
        <div className="grid gap-3 sm:grid-cols-2">
          {fieldVisible('firstName') && (
            <div className="space-y-1">
              <Label htmlFor="reg-fn">{t('COMMON.FIRST_NAME')}</Label>
              <Input
                id="reg-fn"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                aria-invalid={errors?.firstName ? true : undefined}
                autoFocus
              />
            </div>
          )}
          {fieldVisible('lastName') && (
            <div className="space-y-1">
              <Label htmlFor="reg-ln">{t('COMMON.LAST_NAME')}</Label>
              <Input
                id="reg-ln"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                aria-invalid={errors?.lastName ? true : undefined}
              />
            </div>
          )}
        </div>
      )}
      {fieldVisible('company') && (
        <div className="space-y-1">
          <Label htmlFor="reg-comp">{t('REGISTRATION.COMPANY_NAME')}</Label>
          <Input
            id="reg-comp"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            aria-invalid={errors?.company ? true : undefined}
          />
        </div>
      )}
      <div className="space-y-1">
        <Label>
          {t('REGISTRATION.COUNTRY')}
          <span className="text-destructive"> *</span>
        </Label>
        <ShSelect value={country} onValueChange={setCountry}>
          <SelectTrigger
            aria-invalid={errors?.country ? true : undefined}
            className={errors?.country ? 'border-destructive' : undefined}
          >
            <SelectValue placeholder={t('REGISTRATION.SELECT_COUNTRY') ?? ''} />
          </SelectTrigger>
          <SelectContent>
            {countryOptions.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </ShSelect>
        {errors?.country && (
          <p className="text-sm text-destructive">
            {t('REGISTRATION.COUNTRY_REQUIRED') ?? 'Please select a country.'}
          </p>
        )}
      </div>
    </div>
  );

  const addressFields =
    fieldVisible('address') || fieldVisible('city') || fieldVisible('zip') ? (
      <div className="space-y-3">
        {showSectionHeaders && (
          <div className="border-b pb-1">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t('REGISTRATION.SECTION_ADDRESS') ?? 'Adresse'}
            </h3>
          </div>
        )}
        {fieldVisible('address') && (
          <div className="space-y-1">
            <Label htmlFor="reg-addr">{t('REGISTRATION.ADDRESS')}</Label>
            <Input
              id="reg-addr"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              aria-invalid={errors?.address ? true : undefined}
            />
          </div>
        )}
        {(fieldVisible('city') || fieldVisible('zip')) && (
          <div className="grid gap-3 sm:grid-cols-2">
            {fieldVisible('city') && (
              <div className="space-y-1">
                <Label htmlFor="reg-city">{t('REGISTRATION.CITY')}</Label>
                <Input
                  id="reg-city"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  aria-invalid={errors?.city ? true : undefined}
                />
              </div>
            )}
            {fieldVisible('zip') && (
              <div className="space-y-1">
                <Label htmlFor="reg-zip">{t('REGISTRATION.POSTAL_CODE')}</Label>
                <Input
                  id="reg-zip"
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                  aria-invalid={errors?.zip ? true : undefined}
                />
              </div>
            )}
          </div>
        )}
      </div>
    ) : null;

  const authFields = (
    <div className="space-y-3">
      {showSectionHeaders && (
        <div className="border-b pb-1">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {t('REGISTRATION.SECTION_AUTH') ?? 'Connexion'}
          </h3>
        </div>
      )}
      <div className="space-y-1">
        <Label htmlFor="reg-email">{t('COMMON.EMAIL')}</Label>
        <Input
          id="reg-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        {errors?.email && (
          <p className="text-sm text-destructive">Email {errors.email}</p>
        )}
      </div>
      <div className="space-y-1">
        <Label htmlFor="reg-login">{t('COMMON.CHOOSE_LOGIN')}</Label>
        <Input
          id="reg-login"
          value={login}
          onChange={(e) => onLoginChange(e.target.value)}
          required
        />
        {loginRegexError && (
          <p className="text-sm text-destructive">
            {t('ADMIN.CLIENTS.REGISTER_FIELD_REGEX_ERROR')}
          </p>
        )}
        {errors?.login && (
          <p className="text-sm text-destructive">Login {errors.login}</p>
        )}
      </div>
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <Label htmlFor="reg-pwd">{t('COMMON.PASSWORD')}</Label>
          <button
            type="button"
            className="text-xs text-muted-foreground hover:text-foreground"
            onClick={() => setShowPassword((s) => !s)}
          >
            {showPassword ? (
              <EyeOff className="inline h-3.5 w-3.5" />
            ) : (
              <Eye className="inline h-3.5 w-3.5" />
            )}{' '}
            {showPassword ? t('REGISTRATION.HIDE') : t('REGISTRATION.VIEW')}
          </button>
        </div>
        <Input
          id="reg-pwd"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => onPwdChange(e.target.value)}
          required
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="reg-cpwd">{t('REGISTRATION.CONFIRM_PASSWORD')}</Label>
        <Input
          id="reg-cpwd"
          type={showPassword ? 'text' : 'password'}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
        />
        {password && <Hint status={pwdHint} />}
        <p className="text-xs text-muted-foreground">
          {t('REGISTRATION.AUTHORIZED_SPECIAL_CHARACTERS')} !@#$%^&amp;*()-_=+;:'"&lt;,&gt;.
        </p>
        {errors?.password && (
          <p className="text-sm text-destructive">
            {t('REGISTRATION.PASSWORDS_MUST_MEET_THE_PATTERN_AND_BE_THE_SAME')}
          </p>
        )}
      </div>
      <div className="space-y-1">
        <div className="flex items-start gap-2">
          <Checkbox
            id="reg-terms"
            checked={acceptTerms}
            onCheckedChange={(c) => setAcceptTerms(c === true)}
            aria-invalid={errors?.terms ? true : undefined}
          />
          <Label htmlFor="reg-terms" className="text-sm font-normal">
            {t('REGISTRATION.I_HAVE_READ_AND_ACCEPT_THE')}{' '}
            <Link
              to="/cgu"
              target="_blank"
              rel="noreferrer"
              className="text-primary underline"
              onClick={() => pushEvent('click_cgu_from_register')}
            >
              {t('REGISTRATION.TERMS_AND_CONDITIONS')}
            </Link>
          </Label>
        </div>
        {errors?.terms && (
          <p className="text-sm text-destructive">
            {t('REGISTRATION.MUST_ACCEPT_TERMS') ??
              'You must accept the terms and conditions to continue.'}
          </p>
        )}
      </div>
      <ReCAPTCHA
        sitekey={import.meta.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY}
        ref={recaptchaRef}
      />
      {errors?.recaptcha && (
        <p className="text-sm text-destructive">
          {t('REGISTRATION.RECAPTCHA_IS_REQUIRED')}
        </p>
      )}
      <p className="text-[11px] text-muted-foreground">
        {t('REGISTRATION.DRAFT_NOTICE') ??
          'Vos informations sont sauvegardées pour faciliter votre retour. Suppression automatique après 30 jours sans validation.'}
      </p>
    </div>
  );

  const submitLabel = ctaLabel || (t('REGISTRATION.NEXT_STEP') ?? 'Continue');

  return (
    <>
      <form
        noValidate
        onSubmit={
          subStep === 1 && variant === 'steps' ? handleNextFromIdentity : handleSubmit
        }
        className="space-y-5"
      >
        {showIdentitySection && (
          <>
            {identityFields}
            {addressFields}
          </>
        )}
        {showAuthSection && authFields}

        {variant === 'steps' && subStep === 1 ? (
          <Button type="submit" className="w-full gap-2" size="lg">
            {t('REGISTRATION.NEXT') ?? 'Suivant'}
            <ArrowRight className="h-4 w-4" />
          </Button>
        ) : (
          <Button type="submit" disabled={isLoading} className="w-full gap-2" size="lg">
            {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
            {submitLabel}
          </Button>
        )}

        {variant === 'steps' && subStep === 2 && (
          <button
            type="button"
            onClick={() => setSubStep(1)}
            className="mx-auto flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> {t('REGISTRATION.BACK') ?? 'Retour'}
          </button>
        )}
      </form>

      <ConfirmationModal
        showModal={showConfirmModal}
        title={t('REGISTRATION.REDIRECT_MODAL_TITLE')}
        description={t('REGISTRATION.REDIRECT_MODAL_DESCRPTION')}
        handleConfirm={() => navigate('/forgotpassword')}
        handleCancel={() => setShowConfirmModal(false)}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Phone verify step (unchanged behaviour, kept compact)
// ---------------------------------------------------------------------------
type VerifyStepProps = {
  inputs: any;
  setInputs: (i: any) => void;
  errors: any;
  setErrors: (e: any) => void;
  isLoading: boolean;
  setIsLoading: (b: boolean) => void;
  phone: string | undefined;
  setPhone: (p: any) => void;
  loginDisabled: boolean;
  setLoginDisabled: (b: boolean) => void;
  onBack: () => void;
};

function VerifyStep({
  inputs,
  setInputs,
  errors,
  setErrors,
  isLoading,
  setIsLoading,
  phone,
  setPhone,
  loginDisabled,
  setLoginDisabled,
  onBack,
}: VerifyStepProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const setting = (useSelector(registrationSettingSelector) ?? {}) as any;
  const codeSendStatus = useSelector(verificationCodeSendStatusSelector);
  const phoneVerifStatus = useSelector(phoneNumberVerificationStatusSelector);
  const registrationStatus = useSelector(registrationStatusSelector);
  const registrationError = useSelector(registrationErrorSelector);

  const [code, setCode] = useState<string[]>([]);
  const [localErrors, setLocalErrors] = useState<any>(null);

  // Pre-compute synchronously (useMemo, not useEffect) pour que le PhoneInput
  // recoive countries + defaultCountry des le premier render → drapeau affiche.
  const selectedCountryShort = useMemo(() => {
    const c = (setting?.countryList ?? []).find(
      (x: any) => x.countryName === inputs.country,
    );
    return c?.shortName ?? '';
  }, [setting?.countryList, inputs.country]);

  const allowedCountries = useMemo(
    () => (selectedCountryShort ? [selectedCountryShort] : []),
    [selectedCountryShort],
  );

  const showCodeForm = codeSendStatus === SUCCEEDED && phoneVerifStatus !== SUCCEEDED;
  const showSuccess = registrationStatus === SUCCEEDED;

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    const err: any = {};
    if (!phone) {
      err.sendVerificationCodeError =
        t('REGISTRATION.PHONE_REQUIRED') ??
        'Country code and phone number required.';
    }
    if (!_.isEmpty(err)) return setErrors(err);
    // Hint isValidPhoneNumber avec le pays choisi en step 1 → libphonenumber-js
    // tolere les leading 0 (format national francais '07XX' converti en '+337XX')
    // tout en refusant les longueurs / prefixes incorrects.
    // Le widget restreint deja la dropdown au seul pays selectionne (props
    // countries=[X] + countryCallingCodeEditable=false), donc pas besoin d'un
    // check additionnel sur le countryCallingCode parsé.
    if (!isValidPhoneNumber(phone!, selectedCountryShort as any)) {
      err.sendVerificationCodeError =
        t('REGISTRATION.PHONE_INVALID') ?? 'Phone number is not valid.';
    }
    if (!_.isEmpty(err)) return setErrors(err);

    pushEvent('register_phone_send_code');
    setIsLoading(true);
    setInputs({ ...inputs, phone });
    try {
      // @ts-expect-error legacy redux-thunk action types
      await dispatch(sendVerificationCode({ phone }));
    } catch (error: any) {
      errorMessageToast(error?.response?.data?.message);
    }
    setIsLoading(false);
  };

  const handleCodeChange = (idx: number, value: string) => {
    const next = [...code];
    if (value.length > 1) {
      const chars = value.split('').slice(0, 6 - idx);
      chars.forEach((c, i) => {
        next[idx + i] = c;
      });
      const focus = Math.min(5, idx + chars.length);
      document
        .querySelector<HTMLInputElement>(`input[data-code-index='${focus}']`)
        ?.focus();
    } else {
      next[idx] = value;
      if (value && idx < 5) {
        document
          .querySelector<HTMLInputElement>(
            `input[data-code-index='${idx + 1}']`,
          )
          ?.focus();
      }
    }
    setCode(next);
  };

  const handleCodeKeyDown = (
    idx: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === 'Backspace' && !code[idx] && idx > 0) {
      document
        .querySelector<HTMLInputElement>(
          `input[data-code-index='${idx - 1}']`,
        )
        ?.focus();
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    pushEvent('register_phone_verify_code');
    setIsLoading(true);
    const number = Number(code.join(''));
    try {
      // @ts-expect-error legacy redux-thunk action types
      await dispatch(verifyPhoneNumber({ phone, code: number }));
      setLoginDisabled(true);
      pushEvent('register_complete');
    } catch (error) {
      pushEvent('register_phone_verify_failed');
      setLocalErrors(getError(error));
      setIsLoading(false);
      setLoginDisabled(false);
    }
  };

  if (showSuccess) {
    return (
      <div className="space-y-4 text-center">
        <CheckCircle2 className="mx-auto h-16 w-16 text-emerald-500" />
        <h3 className="text-lg font-semibold">
          {t('REGISTRATION.REGISTRATION_SUCCESS') ?? 'Registration successful'}
        </h3>
        <p className="text-sm text-muted-foreground">
          {t('REGISTRATION.YOU_HAVE_SUCCESSFULLY_REGISTERED') ??
            t('REGISTRATION.PLEASE_CHECK_YOUR_EMAIL') ??
            'Please check your email.'}
        </p>
        {!!registrationError && (
          <p className="text-sm text-destructive">{String(registrationError)}</p>
        )}
        <Button asChild className="w-full" size="lg">
          <Link to="/login">{t('LOGIN.SIGN_IN') ?? 'Sign in'}</Link>
        </Button>
      </div>
    );
  }

  if (showCodeForm) {
    const errMsg = localErrors ?? errors?.phoneNumberVerificationError ?? null;
    return (
      <form noValidate onSubmit={handleVerifyCode} className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {t('REGISTRATION.ENTER_CODE_FROM_SMS')}
          {phone && (
            <>
              {' '}
              <span className="font-medium text-foreground">{phone}</span>
            </>
          )}
        </p>
        <div className="space-y-2">
          <Label>{t('REGISTRATION.CODE_FROM_SMS')}</Label>
          <div className="flex gap-2">
            {[0, 1, 2, 3, 4, 5].map((idx) => (
              <Input
                key={idx}
                data-code-index={idx}
                className="h-12 w-12 text-center text-lg"
                value={code[idx] ?? ''}
                onChange={(e) => handleCodeChange(idx, e.target.value)}
                onKeyDown={(e) => handleCodeKeyDown(idx, e)}
                autoFocus={idx === 0}
                maxLength={6}
                required
              />
            ))}
          </div>
          {!_.isEmpty(errMsg) && (
            <p className="text-sm text-destructive">{String(errMsg)}</p>
          )}
        </div>
        <Button
          type="submit"
          disabled={loginDisabled || isLoading}
          className="w-full gap-2"
          size="lg"
        >
          {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
          {t('REGISTRATION.VERIFY_CODE') ?? 'Verify code'}
        </Button>
      </form>
    );
  }

  return (
    <form noValidate onSubmit={handleSendCode} className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {t('REGISTRATION.VERIFY_NUMBER')}
      </p>
      <div className="space-y-1">
        <Label htmlFor="reg-phone">{t('COMMON.PHONE')}</Label>
        <PhoneInput
          // key force le remount si le user revient en step 1 changer de pays
          key={selectedCountryShort || 'no-country'}
          id="reg-phone"
          international
          countryCallingCodeEditable={false}
          // Limite la dropdown au seul pays selectionne en step 1
          countries={
            allowedCountries.length ? (allowedCountries as any) : undefined
          }
          // Affiche le drapeau + prefixe par defaut au lieu d'attendre que
          // l'utilisateur saisisse +33xxx
          defaultCountry={selectedCountryShort || undefined}
          value={phone}
          onChange={setPhone}
          className="phone-input"
        />
        {errors?.sendVerificationCodeError && (
          <p className="text-sm text-destructive">
            {String(errors.sendVerificationCodeError)}
          </p>
        )}
      </div>
      <Button type="submit" disabled={isLoading} className="w-full gap-2" size="lg">
        {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
        {t('REGISTRATION.RECIEVE_THE_CODE') ?? 'Receive code'}
      </Button>
      <button
        type="button"
        onClick={onBack}
        className="mx-auto flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> {t('REGISTRATION.BACK') ?? 'Retour'}
      </button>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Standalone 6-digit code form used by the /verifyNumber email-link
// landing page. Re-exported here for backwards compat with the old
// `import { VerificationCodeForm } from '../Register'` callsite.
// ---------------------------------------------------------------------------
type VerificationCodeFormProps = {
  token?: string | null;
  errors?: any;
  loginDisabled: boolean;
  setLoginDisabled: (b: boolean) => void;
};

export function VerificationCodeForm({
  token,
  errors,
  loginDisabled,
  setLoginDisabled,
}: VerificationCodeFormProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [code, setCode] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [localErrors, setLocalErrors] = useState<any>(null);

  const handleCodeChange = (idx: number, value: string) => {
    const next = [...code];
    if (value.length > 1) {
      const chars = value.split('').slice(0, 6 - idx);
      chars.forEach((c, i) => { next[idx + i] = c; });
      const focus = Math.min(5, idx + chars.length);
      document.querySelector<HTMLInputElement>(`input[data-vc-index='${focus}']`)?.focus();
    } else {
      next[idx] = value;
      if (value && idx < 5) {
        document.querySelector<HTMLInputElement>(`input[data-vc-index='${idx + 1}']`)?.focus();
      }
    }
    setCode(next);
  };

  const handleCodeKeyDown = (
    idx: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === 'Backspace' && !code[idx] && idx > 0) {
      document.querySelector<HTMLInputElement>(`input[data-vc-index='${idx - 1}']`)?.focus();
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    const number = Number(code.join(''));
    try {
      // @ts-expect-error legacy redux-thunk action types
      await dispatch(verifyPhoneNumber({ token, code: number }));
      setLoginDisabled(true);
    } catch (error) {
      setLocalErrors(getError(error));
      setIsLoading(false);
      setLoginDisabled(false);
    }
  };

  const errMsg = localErrors ?? errors?.phoneNumberVerificationError ?? null;

  return (
    <form noValidate onSubmit={handleVerifyCode} className="space-y-4">
      <div className="space-y-2">
        <Label>{t('REGISTRATION.CODE_FROM_SMS')}</Label>
        <div className="flex gap-2">
          {[0, 1, 2, 3, 4, 5].map((idx) => (
            <Input
              key={idx}
              data-vc-index={idx}
              className="h-12 w-12 text-center text-lg"
              value={code[idx] ?? ''}
              onChange={(e) => handleCodeChange(idx, e.target.value)}
              onKeyDown={(e) => handleCodeKeyDown(idx, e)}
              autoFocus={idx === 0}
              maxLength={6}
              required
            />
          ))}
        </div>
        {!_.isEmpty(errMsg) && (
          <p className="text-sm text-destructive">{String(errMsg)}</p>
        )}
      </div>
      <Button
        type="submit"
        disabled={loginDisabled || isLoading}
        className="w-full gap-2"
        size="lg"
      >
        {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
        {t('REGISTRATION.VERIFY_CODE') ?? 'Verify code'}
      </Button>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Top-level Register container
// ---------------------------------------------------------------------------
export default function Register() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  // Variant : default = steps (paginated identity → auth → phone).
  // ?v=sections = legacy 3-section single-page layout, kept around for
  // A/B comparison while we decide to keep it or retire it.
  const variant: Variant =
    searchParams.get('v') === 'sections' ? 'sections' : 'steps';

  const [step, setStep] = useState<1 | 2>(1);
  const [accountSubStep, setAccountSubStep] = useState<1 | 2>(1);
  const [inputs, setInputs] = useState<any>({});
  const [errors, setErrors] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [phone, setPhone] = useState<string | undefined>();
  const [loginDisabled, setLoginDisabled] = useState(false);
  const [contents, setContents] = useState<RegisterContent[]>([]);

  const verificationCodeSendStatus = useSelector(verificationCodeSendStatusSelector);
  const sendVerificationCodeError = useSelector(sendVerificationCodeErrorSelector);
  const phoneNumberVerificationStatus = useSelector(phoneNumberVerificationStatusSelector);

  useEffect(() => {
    pushEvent('register_view', { variant });
    // @ts-expect-error legacy redux-thunk action types
    dispatch(fetchRegistrationSetting(1));
  }, []);

  useEffect(() => {
    let cancelled = false;
    registerPageContentService
      .list()
      .then((rows) => {
        if (!cancelled) setContents((rows as unknown as RegisterContent[]) ?? []);
      })
      .catch(() => {
        if (!cancelled) setContents([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (_.isEmpty(inputs)) return;
    if (!verificationCodeSendStatus) return;
    if (verificationCodeSendStatus === STARTED) return setIsLoading(true);
    if (verificationCodeSendStatus === FAILED) {
      setErrors({ sendVerificationCodeError });
    }
    setIsLoading(false);
  }, [verificationCodeSendStatus]);

  useEffect(() => {
    if (_.isEmpty(inputs)) return;
    if (phoneNumberVerificationStatus !== SUCCEEDED) return;
    (async () => {
      try {
        // @ts-expect-error legacy redux-thunk action types
        await dispatch(registerAction({ ...inputs, language: i18n.language }));
      } catch (error: any) {
        errorMessageToast(error?.response?.data?.message);
      }
    })();
  }, [phoneNumberVerificationStatus]);

  const currentLang = i18n.language?.startsWith('fr') ? 'fr' : 'en';
  const activeContent = useMemo(
    () =>
      contents.find((c) => c.language === currentLang && c.isActive) ??
      contents.find((c) => c.isActive) ??
      null,
    [contents, currentLang],
  );

  // Stepper labels — 2 entries for 'sections' variant, 3 for 'steps' so
  // each sub-step of the account form is a top-level node in the
  // breadcrumb. Phone verification is always the last step.
  const stepperLabels: StepperLabel[] = useMemo(() => {
    if (variant === 'steps') {
      return [
        { key: 1, label: t('REGISTRATION.STEP_IDENTITY') ?? 'Identité' },
        { key: 2, label: t('REGISTRATION.STEP_AUTH') ?? 'Connexion' },
        { key: 3, label: t('REGISTRATION.STEP_VERIFY') ?? 'Vérification' },
      ];
    }
    return [
      { key: 1, label: t('REGISTRATION.STEP_ACCOUNT') ?? 'Compte' },
      { key: 2, label: t('REGISTRATION.STEP_VERIFY') ?? 'Vérification' },
    ];
  }, [variant, t]);

  const stepperCurrent = useMemo(() => {
    if (variant === 'steps') {
      if (step === 1) return accountSubStep; // 1 or 2
      return 3;
    }
    return step;
  }, [variant, step, accountSubStep]);

  return (
    <div className="relative grid min-h-screen grid-cols-1 md:grid-cols-12">
      <TopBar
        contents={contents}
        currentLang={currentLang}
        onLangChange={(lang) => i18n.changeLanguage(lang)}
      />

      {/* Left — form */}
      <div className="flex min-h-screen flex-col bg-background p-6 md:col-span-5 md:p-10 lg:p-14">
        <div className="mb-8">
          <img src={LogoLight} alt="SMSReflex" className="h-12 w-auto dark:hidden" />
          <img src={LogoDark} alt="SMSReflex" className="hidden h-12 w-auto dark:block" />
        </div>

        <div className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center">
          <h2 className="text-xl font-semibold">
            {t('REGISTRATION.REGISTER') ?? 'Register'}
          </h2>
          <p className="mt-1 mb-6 text-sm text-muted-foreground">
            {t('REGISTRATION.PLEASE_FILL_REGISTRATION_FORM')}
          </p>

          <Stepper current={stepperCurrent} labels={stepperLabels} />

          {step === 1 && (
            <AccountStep
              inputs={inputs}
              setInputs={setInputs}
              errors={errors}
              setErrors={setErrors}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              onAccountValidated={() => setStep(2)}
              variant={variant}
              ctaLabel={activeContent?.ctaText}
              onSubStepChange={setAccountSubStep}
            />
          )}

          {step === 2 && (
            <VerifyStep
              inputs={inputs}
              setInputs={setInputs}
              errors={errors}
              setErrors={setErrors}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              phone={phone}
              setPhone={setPhone}
              loginDisabled={loginDisabled}
              setLoginDisabled={setLoginDisabled}
              onBack={() => setStep(1)}
            />
          )}

          <p className="mt-6 text-center text-sm text-muted-foreground">
            {t('LOGIN.ALREADY_HAVE_ACCOUNT') ?? 'Already have an account?'}{' '}
            <Link
              to="/login"
              className="text-primary hover:underline"
              onClick={() => pushEvent('click_login_link_from_register')}
            >
              {t('LOGIN.SIGN_IN') ?? 'Sign in'}
            </Link>
          </p>
        </div>
      </div>

      {/* Right — marketing column */}
      <MarketingColumn content={activeContent} />
    </div>
  );
}
